/**
 * Desktop PDP layout-shift attribution (2026-10 perf plan, P1-1).
 *
 * Shopify's RUM says desktop product pages hit CLS ~1.0 at p75 since Sep 9, but it can't
 * say which element moves, and nothing reproduces it in the lab (fresh headless visitors
 * score 0.05-0.26 on load, scroll, hover). This records it from real visitors instead.
 *
 * Contract:
 *   - Runs only on product pages at >= 990px (rendered from layout/theme.liquid), for a
 *     SAMPLE of page views, so it costs nothing for most visitors.
 *   - Computes CLS the web-vitals way: shifts without recent input, grouped in session
 *     windows (gap < 1s, window < 5s); the score is the worst window.
 *   - When the page is hidden and the score is >= THRESHOLD, sends ONE Sentry message,
 *     fingerprinted 'pdp-cls' so every report lands in one issue. Extra data: the worst
 *     window's shifts, each with time, value, scrollY, and up to 5 moved elements with
 *     before/after rects flattened into ONE string (`a || b`), because nested arrays get
 *     cut to "[Array]" by Sentry's normalizeDepth. No personal data.
 *   - Never throws, never sends twice, does nothing if Sentry isn't on the page.
 *
 * Remove once the cause is found and fixed; it is diagnostic only.
 */
(function () {
  // ?cls-debug: always run, any score, and console.log the report instead of sending it.
  var DEBUG = /[?&]cls-debug(=|&|$)/.test(window.location.search);
  var SAMPLE = DEBUG ? 1 : 0.25;
  var THRESHOLD = DEBUG ? 0 : 0.25;
  if (window.innerWidth < 990 || !('PerformanceObserver' in window) || Math.random() >= SAMPLE) return;

  function describe(node) {
    if (!node) return '(removed)';
    if (node.nodeType !== 1) return '#text in ' + describe(node.parentElement);
    var s = node.tagName.toLowerCase();
    if (node.id) s += '#' + node.id;
    if (typeof node.className === 'string' && node.className.trim()) {
      s += '.' + node.className.trim().split(/\s+/).slice(0, 3).join('.');
    }
    var p = node.parentElement;
    if (p) {
      var q = p.tagName.toLowerCase();
      if (p.id) q += '#' + p.id;
      else if (typeof p.className === 'string' && p.className.trim()) q += '.' + p.className.trim().split(/\s+/)[0];
      s = q + ' > ' + s;
    }
    return s;
  }
  function rect(r) {
    return Math.round(r.x) + ',' + Math.round(r.y) + ' ' + Math.round(r.width) + 'x' + Math.round(r.height);
  }

  var windowValue = 0, windowFirst = 0, windowLast = 0, windowShifts = [];
  var worst = 0, worstShifts = [];
  var start = performance.now();

  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (e) {
        if (e.hadRecentInput) return;
        var shift = {
          t: Math.round(e.startTime),
          v: Math.round(e.value * 1000) / 1000,
          scrollY: Math.round(window.scrollY),
          // One string, not an array: Sentry's default normalizeDepth (3) turns an array at
          // extra.worst_window[i].sources into the literal "[Array]" and the data is lost.
          sources: (e.sources || []).map(function (s) {
            return describe(s.node) + ' | ' + rect(s.previousRect) + ' -> ' + rect(s.currentRect);
          }).join(' || '),
        };
        if (windowValue && e.startTime - windowLast < 1000 && e.startTime - windowFirst < 5000) {
          windowValue += e.value;
          windowShifts.push(shift);
        } else {
          windowValue = e.value;
          windowFirst = e.startTime;
          windowShifts = [shift];
        }
        windowLast = e.startTime;
        if (windowValue > worst) {
          worst = windowValue;
          worstShifts = windowShifts.slice(0, 12);
        }
      });
    }).observe({ type: 'layout-shift', buffered: true });
  } catch (err) {
    return;
  }

  var sent = false;
  function report() {
    if (sent || worst < THRESHOLD) return;
    if (DEBUG) {
      sent = true;
      console.log('[cls-attribution]', JSON.stringify({ cls: worst, worst_window: worstShifts }));
      return;
    }
    if (!window.Sentry || typeof Sentry.withScope !== 'function') return;
    sent = true;
    try {
      Sentry.withScope(function (scope) {
        scope.setFingerprint(['pdp-cls']);
        scope.setLevel('info');
        scope.setTag('cls_bucket', worst >= 1 ? '1+' : worst >= 0.5 ? '0.5-1' : '0.25-0.5');
        scope.setTag('viewport', window.innerWidth + 'x' + window.innerHeight);
        scope.setExtra('cls', Math.round(worst * 1000) / 1000);
        scope.setExtra('time_on_page_s', Math.round((performance.now() - start) / 1000));
        scope.setExtra('worst_window', worstShifts);
        scope.setExtra('visually', !!document.getElementById('vslyAntiFlickerStyle-AE29') || typeof window.loomi_ctx !== 'undefined');
        scope.setExtra('navigation', (performance.getEntriesByType('navigation')[0] || {}).type || '');
        Sentry.captureMessage('PDP desktop layout shift');
      });
    } catch (err) {
      /* diagnostic only */
    }
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') report();
  });
  window.addEventListener('pagehide', report);
  if (DEBUG) window.__clsReport = report;
})();
